import { isTauri } from '@/tauri/utils';
import { getDesktopInstallerType } from '@/utils/appConfiguration';
import { getDesktopBuildId } from '@/utils/appVersion';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import {
  enqueueDatadogClientDiagnosticsAction,
  getDatadogClientStatus,
} from '@/utils/tracing';

const REPORT_VERSION = 1;

/** Long numeric IDs, UUIDs, and 24-char MongoDB ObjectIds. */
const OPAQUE_ID_SEGMENT =
  /^(?:\d{16,}|[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}|[0-9a-f]{24})$/i;

/** Haiku region codes such as `us`, `au`, `eun1`. */
const HAIKU_REGION_SEGMENT = /^[a-z]{2,5}\d?$/i;

export type ClientSdkDiagnosticsReport = {
  report_version: typeof REPORT_VERSION;
  webview_origin: string;
  page_path: string;
  flagsmith: {
    provider_status: string;
    resolved: boolean;
  };
  datadog: {
    configured: boolean;
    rum_initialized: boolean;
    logs_initialized: boolean;
    rum_action_queued: boolean;
    transport_probe: 'reachable' | 'failed' | 'not_attempted';
  };
  build: {
    environment: string;
    release: string;
    platform: 'desktop' | 'web';
    installer_type: string;
    build_id?: string;
  };
};

const DATADOG_PROBE_TIMEOUT_MS = 5_000;

/**
 * Strip query/hash and redact path segments that can carry PHI or opaque
 * launch identifiers before the value reaches backend diagnostics logs.
 */
export const sanitizePagePath = (pathname: string): string => {
  const segments = pathname.split('/');
  const haikuIndex = segments.indexOf('haiku');

  const sanitizedPath = segments
    .map((segment, index) => {
      if (!segment) return segment;

      const previous = segments[index - 1];
      const previous2 = segments[index - 2];

      // /s/[token] and /scribe/chat/s/[token]
      if (previous === 's') return ':token';

      // /shared/sessions/[token], /shared/templates/[id]
      if (
        previous2 === 'shared' &&
        (previous === 'sessions' || previous === 'templates')
      ) {
        return ':id';
      }

      // /integration/epic/haiku/[region]/[profile_name]/[external_session_id]
      // Keep `complete` and short region codes; redact profile names + ids.
      if (haikuIndex >= 0 && index > haikuIndex) {
        if (segment === 'complete') return segment;
        if (index === haikuIndex + 1 && HAIKU_REGION_SEGMENT.test(segment)) {
          return segment;
        }
        return ':id';
      }

      if (OPAQUE_ID_SEGMENT.test(segment)) return ':id';
      return segment;
    })
    .join('/');

  return sanitizedPath.slice(0, 512) || '/';
};

const getPagePath = () => {
  if (typeof window === 'undefined') return '/';
  return sanitizePagePath(window.location.pathname);
};

const getWebviewOrigin = () => {
  if (typeof window === 'undefined') return 'unknown';
  return window.location.origin.slice(0, 256);
};

const probeDatadogTransport = async (
  configured: boolean
): Promise<'reachable' | 'failed' | 'not_attempted'> => {
  // TELEMETRY-GATE: F2 — never probe https://sys.heidihealth.com from a
  // suppressed build.
  if (!isSaasTelemetryEnabled) return 'not_attempted';
  if (!configured || typeof window === 'undefined') return 'not_attempted';

  const datadogSite = process.env.NEXT_PUBLIC_DATADOG_SITE || 'datadoghq.com';
  const region =
    datadogSite === 'datadoghq.com'
      ? undefined
      : datadogSite === 'datadoghq.eu'
        ? 'eu'
        : datadogSite.split('.', 1)[0];
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), DATADOG_PROBE_TIMEOUT_MS);

  try {
    // A no-cors request deliberately distinguishes browser/network blocking
    // (rejects) from a reachable proxy without exposing its response body.
    await fetch(`https://sys.heidihealth.com${region ? `/rum/${region}` : ''}/`, {
      method: 'GET',
      mode: 'no-cors',
      credentials: 'omit',
      cache: 'no-store',
      signal: controller.signal,
    });
    return 'reachable';
  } catch {
    return 'failed';
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const collectClientSdkStatus = async ({
  flagsProviderStatus,
  flagsResolved,
}: {
  flagsProviderStatus: string;
  flagsResolved: boolean;
}): Promise<ClientSdkDiagnosticsReport> => {
  const datadog = getDatadogClientStatus();
  const isDesktop = isTauri();
  const transportProbe = await probeDatadogTransport(datadog.configured);

  return {
    report_version: REPORT_VERSION,
    webview_origin: getWebviewOrigin(),
    page_path: getPagePath(),
    flagsmith: {
      provider_status: flagsProviderStatus,
      resolved: flagsResolved,
    },
    datadog: {
      configured: datadog.configured,
      rum_initialized: datadog.rumInitialized,
      logs_initialized: datadog.logsInitialized,
      rum_action_queued: enqueueDatadogClientDiagnosticsAction(),
      transport_probe: transportProbe,
    },
    build: {
      environment: process.env.NEXT_PUBLIC_ENVIRONMENT || 'unknown',
      release:
        [process.env.NEXT_PUBLIC_RELEASE_REF, process.env.NEXT_PUBLIC_RELEASE_SHA]
          .filter(Boolean)
          .join('-') || 'unknown',
      platform: isDesktop ? 'desktop' : 'web',
      installer_type: getDesktopInstallerType(),
      ...(getDesktopBuildId() ? { build_id: getDesktopBuildId() } : {}),
    },
  };
};
